import { useState } from "react";
import { createUserWithEmailAndPassword, signOut, updateProfile } from "firebase/auth";
import { auth, db } from "../firebase/firebase";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import styles from "./AuthForm.module.css";
import { useTranslation } from 'react-i18next';
import TermsAcceptance from "../components/terms/TermsAcceptance";
import { TEACHER_STATUS } from "../utils/teacherStatus";

const Register = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "student",
  });
  const [passwordError, setPasswordError] = useState("");
  const [termsError, setTermsError] = useState("");
  const [formError, setFormError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [teacherPending, setTeacherPending] = useState(false);

  const navigate = useNavigate();

  const registerHandler = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setTermsError("");
    setFormError("");

    if (!termsAccepted) {
      setTermsError(t("Register.termsRequired"));
      return;
    }

    if (data.password.length < 8) {
      setPasswordError("Password must be at least 8 characters long.");
      return;
    }

    try {
      const cred = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );

      await updateProfile(cred.user, {
        displayName: `${data.firstName} ${data.lastName}`,
      });

      const isTeacher = data.role === "teacher";
      const payload = {
        userId: cred.user.uid,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        role: data.role,
        profileImage: null,
        termsAcceptedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };

      if (isTeacher) {
        payload.status = TEACHER_STATUS.PENDING;
      }

      await setDoc(doc(db, isTeacher ? "teachers" : "students", cred.user.uid), payload);

      if (isTeacher) {
        await signOut(auth);
        setTeacherPending(true);
        return;
      }

      navigate("/login");
    } catch (err) {
      setFormError(err.message || t("Register.registerFailed"));
    }
  };

  if (teacherPending) {
    return (
      <div className={styles.authPage}>
        <div className={styles.authCard}>
          <h2 className={styles.title}>{t("Register.pendingTitle")}</h2>
          <p className={styles.success}>{t("Register.pendingBody")}</p>
          <button
            className={styles.button}
            type="button"
            onClick={() => navigate("/login")}
          >
            {t("Register.login")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.authPage}>
      <div className={styles.authCard}>
        <h2 className={styles.title}>{t('Register.title')}</h2>

        <form className={styles.form} onSubmit={registerHandler}>
          <input
            className={styles.input}
            placeholder={t('Register.firstName')}
            onChange={(e) =>
              setData({ ...data, firstName: e.target.value })
            }
          />

          <input
            className={styles.input}
            placeholder={t('Register.lastName')}
            onChange={(e) =>
              setData({ ...data, lastName: e.target.value })
            }
          />

          <input
            className={styles.input}
            placeholder={t('Register.email')}
            onChange={(e) =>
              setData({ ...data, email: e.target.value })
            }
          />

          <input
            className={styles.input}
            type="password"
            placeholder={t('Register.password')}
            minLength={8}
            required
            onChange={(e) => {
              setData({ ...data, password: e.target.value });
              setPasswordError("");
            }}
          />
          {passwordError && <p className={styles.error}>{passwordError}</p>}

          <select
            className={styles.select}
            value={data.role}
            onChange={(e) =>
              setData({ ...data, role: e.target.value })
            }
          >
            <option value="student">{t('Register.student')}</option>
            <option value="teacher">{t('Register.teacher')}</option>
          </select>
          {data.role === "teacher" && (
            <p className={styles.footerText}>{t("Register.teacherApprovalNote")}</p>
          )}

          <TermsAcceptance
            accepted={termsAccepted}
            onAcceptedChange={(value) => {
              setTermsAccepted(value);
              setTermsError("");
            }}
          />
          {termsError && <p className={styles.error}>{termsError}</p>}
          {formError && <p className={styles.error}>{formError}</p>}

          <button
            className={styles.button}
            type="submit"
            disabled={!termsAccepted}
          >
            {t('Register.registerButton')}
          </button>
        </form>

        <p className={styles.footerText}>
          {t('Register.alreadyHaveAccount')}{" "}
          <span className={styles.link} onClick={() => navigate("/login")}>
            {t('Register.login')} 
          </span>
        </p>
      </div>
    </div>
  );
};

export default Register;
